"use client";

import React, { useState } from "react";
import { CalendarEvent } from "../../types/calendar";
import { EventDetailsTab } from "./EventDetailsTab";
import { EventActionsTab } from "./EventActionsTab";
import { EventDiagnosticsTab } from "./EventDiagnosticsTab";
import { EventCRMTab } from "./EventCRMTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../tabs";
import { Calendar, Users, Settings, Bug } from "lucide-react";

interface EventDetailsTabsProps {
  event: CalendarEvent;
  onEdit?: (event: CalendarEvent) => void;
  onDelete?: (event: CalendarEvent) => void;
  onClose?: () => void;
  isPageView?: boolean;
  // CRM Integration props
  onLogActivity?: (activityData: any) => Promise<void>;
  onComposeEmail?: (recipients: string[], subject?: string) => void;
  // Custom CRM tab content
  customCRMTab?: React.ReactNode;
}

export function EventDetailsTabs({
  event,
  onEdit,
  onDelete,
  onClose,
  isPageView = false,
  onLogActivity,
  onComposeEmail,
  customCRMTab,
}: EventDetailsTabsProps) {
  const [activeTab, setActiveTab] = useState("details");

  // Colors simplified across the tabs for a more neutral look

  const tabs = [
    {
      id: "details",
      label: "Details",
      icon: Calendar,
      color: "text-slate-600",
      content: (
        <EventDetailsTab
          event={event}
          onEdit={onEdit}
          onDelete={onDelete}
          isPageView={isPageView}
        />
      ),
    },
    {
      id: "crm",
      label: "CRM / Attendees",
      icon: Users,
      color: "text-slate-600",
      content: customCRMTab || (
        <EventCRMTab
          event={event}
          onLogActivity={onLogActivity}
          onComposeEmail={onComposeEmail}
          isPageView={isPageView}
        />
      ),
      disabled: !customCRMTab && !onLogActivity && !onComposeEmail,
    },
    {
      id: "actions",
      label: "Actions",
      icon: Settings,
      color: "text-slate-600",
      content: (
        <EventActionsTab
          event={event}
          onEdit={onEdit}
          onDelete={onDelete}
          onClose={onClose}
          isPageView={isPageView}
        />
      ),
    },
    {
      id: "diagnostics",
      label: "Debug",
      icon: Bug,
      color: "text-slate-600",
      content: (
        <EventDiagnosticsTab
          event={event}
        />
      ),
      disabled: process.env.NODE_ENV === 'production',
    },
  ];

  const visibleTabs = tabs.filter(tab => !tab.disabled);

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className={`grid w-full ${visibleTabs.length === 2 ? 'grid-cols-2' : visibleTabs.length === 3 ? 'grid-cols-3' : 'grid-cols-4'} bg-card border border-gray-200`}>
        {visibleTabs.map((tab) => (
          <TabsTrigger
            key={tab.id}
            value={tab.id}
            className="flex items-center space-x-2 data-[state=active]:bg-card data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-gray-300"
          >
            <tab.icon className={`h-4 w-4 ${tab.color}`} />
            <span className="hidden sm:inline font-medium">{tab.label}</span>
          </TabsTrigger>
        ))}
      </TabsList>
      
      {visibleTabs.map((tab) => (
        <TabsContent key={tab.id} value={tab.id} className="mt-6">
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  );
}
